import dayjs from 'dayjs';
import { useCallback } from 'react';
import type { Stages } from '../../api/process-client/process-client.type';
import type { Task } from '../../api/tasks-client/task-client.type';
import { useGlobalStore } from '../global-store/global-store';
import { useProcessStoreUpdate } from '../process-store/process-store';
import type {
  EventScope,
  ProcessScope,
  ProcessWorkflow,
  TaskSearchHistoryByPortfolio,
} from '../process-store/process-store.type';
/**
 * Custom hook to easily access and update the process store
 * It mimics the same API as the ProcessContext for easier migration
 *
 * The Task-search history fields are returned for completeness; prefer
 * `useTaskSearchHistory`, which scopes them to the active portfolio and
 * subscribes only to that slice.
 */
const useProcessStore = () => {
  const {
    scope,
    setScope,
    stages,
    setStages,
    workflow,
    setWorkflow,
    eventScope,
    setEventScope,
    isComparisonView,
    setIsComparisonView,
    rightScope,
    setRightScope,
    rightStages,
    setRightStages,
    rightWorkflow,
    setRightWorkflow,
    rightEventScope,
    setRightEventScope,
    taskSearchHistoryByPortfolio,
    addTaskSearchHistoryEntry,
    removeTaskSearchHistoryEntry,
    clearTaskSearchHistory,
  } = useProcessStoreUpdate();

  // We return the same interface as ProcessContext to make migration easier
  return {
    scope,
    setScope,
    stages,
    setStages,
    workflow,
    setWorkflow,
    eventScope,
    setEventScope,
    isComparisonView,
    setIsComparisonView,
    rightScope,
    setRightScope,
    rightStages,
    setRightStages,
    rightWorkflow,
    setRightWorkflow,
    rightEventScope,
    setRightEventScope,
    taskSearchHistoryByPortfolio,
    addTaskSearchHistoryEntry,
    removeTaskSearchHistoryEntry,
    clearTaskSearchHistory,
  };
};

/**
 * Select specific parts of the process store with proper memoization
 * This helps prevent unnecessary re-renders
 */
const useProcessSelector = <T>(
  selector: (state: {
    scope: ProcessScope;
    stages: Stages[];
    workflow: ProcessWorkflow | null;
    eventScope: EventScope;
    isComparisonView: boolean;
    rightScope: ProcessScope;
    rightStages: Stages[];
    rightWorkflow: ProcessWorkflow | null;
    rightEventScope: EventScope;
    taskSearchHistoryByPortfolio: TaskSearchHistoryByPortfolio;
  }) => T,
): T => {
  return useProcessStoreUpdate(selector);
};

/**
 * Resets the process store to its default state
 *
 * This function:
 * - Clears the cached process store from localStorage
 * - Resets all state values to their defaults for both primary and comparison views
 * - Resets dates to default range (current month to 1 month prior)
 * - Clears all selections including portfolio, team, board, workflow, and event scopes
 * - Leaves the Task-search history alone: it is keyed by portfolio and must
 *   survive the portfolio switch that triggers this reset
 *
 * Used when switching portfolios or when a complete reset of the process view is needed.
 */

const resetProcessStore = () => {
  const store = useProcessStoreUpdate.getState();

  // Remove the cached process store from localStorage
  localStorage.removeItem('process-storage');
  localStorage.removeItem('process-scope');

  // Reset all state in the store to defaults
  store.setScope({
    portfolio: null,
    team: null,
    board: null,
    epic: null,
    task: null,
    workflow: null,
    startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
    endDate: dayjs().toISOString(),
    taskTypes: null,
    useWorkflow: false,
    useGroups: true,
  });

  store.setStages([]);
  store.setWorkflow(null);
  store.setEventScope({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  // Reset right side store too
  store.setRightScope({
    portfolio: null,
    team: null,
    board: null,
    epic: null,
    task: null,
    workflow: null,
    startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
    endDate: dayjs().toISOString(),
    taskTypes: null,
    useWorkflow: false,
    useGroups: true,
  });

  store.setRightStages([]);
  store.setRightWorkflow(null);
  store.setRightEventScope({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  store.setIsComparisonView(false);
};

const EMPTY_TASK_SEARCH_HISTORY: Task[] = [];

/**
 * Reads and edits the recent Task-search picks for the active portfolio.
 *
 * There is one list per portfolio, keyed by the global store's portfolio id,
 * and every consumer of this hook sees the same one. With no portfolio
 * selected the list is empty and writes are dropped. The returned callbacks
 * keep their identity until the portfolio changes.
 *
 * @returns {{ entries: Task[]; addEntry: (task: Task) => void; removeEntry: (taskId: string) => void; clearEntries: () => void }}
 *   The portfolio's picks, most recent first, and bound actions that take the portfolio into account
 */
const useTaskSearchHistory = () => {
  const portfolioId = useGlobalStore((state) => state.portfolio?.id ?? null);
  const entries =
    useProcessStoreUpdate((state) => (portfolioId ? state.taskSearchHistoryByPortfolio[portfolioId] : undefined)) ??
    EMPTY_TASK_SEARCH_HISTORY;
  const addTaskSearchHistoryEntry = useProcessStoreUpdate((state) => state.addTaskSearchHistoryEntry);
  const removeTaskSearchHistoryEntry = useProcessStoreUpdate((state) => state.removeTaskSearchHistoryEntry);
  const clearTaskSearchHistory = useProcessStoreUpdate((state) => state.clearTaskSearchHistory);

  const addEntry = useCallback(
    (task: Task) => {
      if (portfolioId) {
        addTaskSearchHistoryEntry(portfolioId, task);
      }
    },
    [addTaskSearchHistoryEntry, portfolioId],
  );

  const removeEntry = useCallback(
    (taskId: string) => {
      if (portfolioId) {
        removeTaskSearchHistoryEntry(portfolioId, taskId);
      }
    },
    [portfolioId, removeTaskSearchHistoryEntry],
  );

  const clearEntries = useCallback(() => {
    if (portfolioId) {
      clearTaskSearchHistory(portfolioId);
    }
  }, [clearTaskSearchHistory, portfolioId]);

  return { entries, addEntry, removeEntry, clearEntries };
};

export { resetProcessStore, useProcessSelector, useProcessStore, useTaskSearchHistory };
